import { useState } from 'react';
import type { FC } from 'react';

interface FooterVersionProps {
  version: string;
  copyrightText?: string;
}

export const FooterVersion: FC<FooterVersionProps> = ({
  version,
  copyrightText = `© ${new Date().getFullYear()} AI Ad generator`
}) => {
  const [showSecretInput, setShowSecretInput] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const toggleSecretInput = () => {
    setShowSecretInput(!showSecretInput);
    // Reset states when toggling
    if (!showSecretInput) {
      setGeneratedImage(null);
      setError(null);
    }
  };
  
  const generateImage = async () => {
    if (!imagePrompt.trim()) {
      setError('Please enter a prompt for the image');
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    
    try {
      const response = await fetch('/api/generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: imagePrompt }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate image');
      }
      
      const data = await response.json();
      setGeneratedImage(data.imageUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate image');
      console.error('Error generating image:', err);
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <footer className="max-w-4xl mx-auto mt-8 text-center text-sm text-gray-500">
      <div className="mb-2">
        <p className="text-gray-400">
          <span 
            className="text-xs cursor-pointer hover:text-gray-700 transition-colors"
            onClick={toggleSecretInput}
          >
            v{version}
          </span>
        </p>
      </div>
      
      {showSecretInput && (
        <div className="mt-4 p-4 bg-gray-100 rounded-lg shadow-inner max-w-md mx-auto">
          <h3 className="text-base font-medium text-gray-700 mb-2">Secret Image Generator</h3>
          <div className="mb-3">
            <input
              type="text"
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              placeholder="Enter prompt for portrait image..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isGenerating}
            />
          </div>
          <button
            onClick={generateImage}
            disabled={isGenerating || !imagePrompt.trim()}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? 'Generating...' : 'Generate Portrait Image'}
          </button>
          
          {error && (
            <div className="mt-3 p-2 bg-red-50 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}
          
          {generatedImage && (
            <div className="mt-4">
              <img 
                src={generatedImage} 
                alt="Generated portrait" 
                className="mx-auto rounded-md shadow-md max-h-96 max-w-full"
              />
            </div>
          )}
        </div>
      )}
      
      <p className="mt-2">{copyrightText}</p>
    </footer>
  );
};
